<template>
  <div class="page-wrap">
    <!-- 导航栏 -->
    <Navbar />

    <!-- 主体区域 -->
    <div class="main-content">
      <div class="container">
        <div class="recognition-wrapper">
          <!-- <h2 class="page-title">项目价格</h2> -->

          <!-- 模型列表视图 -->
          <div v-if="!selectedModel">
            <!-- 搜索框 -->
            <div class="search-box">
              <input
                v-model="keyword"
                type="text"
                placeholder="搜索模型名称或支持的平台..."
                class="search-input"
                @input="onSearch"
              />
            </div>

            <!-- 分类 Tab -->
            <div class="category-tabs">
              <span
                class="tab-item"
                :class="{ active: activeCategory === 0 }"
                @click="switchCategory(0)"
              >全部类型</span>
              <span
                v-for="cat in categories"
                :key="cat.id"
                class="tab-item"
                :class="{ active: activeCategory === cat.id }"
                @click="switchCategory(cat.id)"
              >{{ cat.name }}</span>
            </div>

            <!-- 模型列表 -->
            <div class="model-list" v-if="filteredModels.length > 0">
              <div
                v-for="model in filteredModels"
                :key="model.id"
                class="model-card"
              >
                <!-- 模型名称 + 适用平台 -->
                <div class="model-header" @click="selectModel(model)">
                  <span class="model-name">{{ model.name }}</span>
                  <span v-if="model.platforms.length > 0" class="header-platforms">
                    <span v-for="p in model.platforms" :key="p" class="platform-chip">{{ p }}</span>
                  </span>
                  <span v-else class="header-no-platform">暂无适用平台</span>
                </div>

                <!-- 示例图网格 -->
                <div class="model-images" @click="selectModel(model)">
                  <img
                    v-for="(img, idx) in model.images"
                    :key="idx"
                    :src="img"
                    class="sample-img"
                    @error="onImageError($event)"
                  />
                  <div v-if="model.images.length === 0" class="no-images">暂无示例图</div>
                </div>

                <!-- 底部信息栏 -->
                <div class="model-footer">
                  <div class="model-meta">
                    <span class="meta-item">
                      <span class="meta-label">类型</span>
                      <span class="meta-value">{{ getCategoryName(model.category_id) || '未分类(ID:' + model.category_id + ')' }}</span>
                    </span>
                    <span class="meta-divider"></span>
                    <span class="meta-item">
                      <span class="meta-label">描述</span>
                      <span class="meta-value desc-text">{{ model.description || '暂无描述' }}</span>
                    </span>
                    <span class="meta-divider"></span>
                    <span class="meta-item">
                      <span class="meta-label">价格</span>
                      <span class="meta-value price">¥{{ (model.price || 0).toFixed(2) }}/次</span>
                    </span>
                  </div>
                  <div class="model-actions">
                    <van-button
                      type="primary"
                      size="small"
                      @click.stop="selectModel(model)"
                    >测试</van-button>
                    <van-button
                      plain
                      size="small"
                      @click.stop="openDocs(model)"
                    >开发文档</van-button>
                  </div>
                </div>
              </div>
            </div>

            <!-- 空状态 -->
            <div v-else class="empty-state">
              <div class="empty-icon">🔍</div>
              <div class="empty-text">暂无匹配的模型</div>
            </div>
          </div>

          <!-- 测试视图（选择模型后展示） -->
          <div v-if="selectedModel" class="test-view">
            <!-- 面包屑导航 -->
            <div class="breadcrumb">
              <a @click="backToList">产品及测试</a>
              <span class="bc-sep">/</span>
              <span class="bc-current">{{ selectedModel.name }}</span>
            </div>

            <!-- 模型信息卡 -->
            <div class="test-model-card">
              <div class="test-card-left">
                <h2 class="test-model-name">{{ selectedModel.name }}</h2>
                <div class="test-model-tags">
                  <span class="test-tag type-tag">类型：{{ getCategoryName(selectedModel.category_id) || '未分类' }}</span>
                  <span class="test-tag price-tag">价格：¥{{ (selectedModel.price || 0).toFixed(4) }}/次</span>
                </div>
                <div v-if="selectedModel.description" class="test-model-desc">{{ selectedModel.description }}</div>
                <div v-if="selectedModel.platforms.length > 0" class="test-platforms">
                  <span class="tp-label">适用平台：</span>
                  <span v-for="p in selectedModel.platforms" :key="p" class="tp-chip">{{ p }}</span>
                </div>
              </div>
              <div class="test-card-right">
                <div class="test-sample-grid" v-if="selectedModel.images.length > 0">
                  <img
                    v-for="(img, idx) in selectedModel.images"
                    :key="idx"
                    :src="img"
                    class="test-sample-img"
                    @error="onImageError($event)"
                  />
                </div>
                <div v-else class="test-no-sample">暂无示例图</div>
              </div>
            </div>

            <!-- 在线测试区域（放在接口说明上方） -->
            <div class="api-info-section test-section">
              <h3 class="section-title">在线测试</h3>

              <!-- 图片上传 -->
              <div class="test-upload-area" @click="triggerUpload" @dragover.prevent @drop.prevent="handleDrop">
                <img v-if="previewUrl" :src="previewUrl" class="test-preview-image" />
                <div v-else class="test-upload-placeholder">
                  <svg class="upload-svg" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect x="4" y="8" width="40" height="32" rx="4" stroke="#0066ff" stroke-width="2" fill="none"/>
                    <path d="M24 18v12M18 24l6-6 6 6" stroke="#0066ff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <p class="upload-main-text">点击或拖拽图片到此区域上传</p>
                  <p class="upload-sub-text">支持 JPG / PNG / GIF / BMP，最大 5MB</p>
                </div>
                <input type="file" ref="fileInput" accept="image/*" style="display:none" @change="handleFileChange" />
              </div>

              <!-- 识别按钮 -->
              <div class="test-action-row">
                <van-button
                  type="primary"
                  size="large"
                  :loading="loading"
                  :disabled="!selectedFile"
                  @click="doRecognize"
                  class="test-btn"
                >
                  {{ loading ? '识别中...' : '开始识别' }}
                </van-button>
                <van-button
                  v-if="selectedFile"
                  plain
                  size="large"
                  @click="clearImage"
                  class="test-btn"
                >清除图片</van-button>
              </div>

              <!-- 识别结果 -->
              <div class="test-result-area">
                <div class="test-result-header">
                  <span>识别结果</span>
                </div>
                <div class="test-result-body">
                  <!-- 结果图片（带标注框） -->
                  <div v-if="resultImageUrl" class="test-result-image-wrap">
                    <img :src="resultImageUrl" class="test-result-image" />
                    <div v-if="apiResults.length > 0" class="test-result-count">共识别出 <strong>{{ apiResults.length }}</strong> 个目标</div>
                  </div>

                  <!-- 结果列表 -->
                  <div v-if="apiResults.length > 0" class="test-result-list">
                    <div v-for="(item, index) in apiResults" :key="index" class="test-result-item">
                      <span class="tri-index">#{{ index + 1 }}</span>
                      <span class="tri-char">{{ item.char || item.text || '-' }}</span>
                      <span class="tri-conf">{{ ((item.confidence || 0) * 100).toFixed(0) }}%</span>
                      <span v-if="item.center_x !== undefined" class="tri-coord">X:{{ item.center_x }} Y:{{ item.center_y }}</span>
                    </div>
                  </div>

                  <!-- 纯文本结果 -->
                  <div v-if="resultText && apiResults.length === 0" class="test-result-text">
                    {{ resultText }}
                  </div>

                  <!-- 无结果 -->
                  <div v-if="!resultText && !resultImageUrl && !errorMsg && !loading" class="test-result-empty">
                    暂无结果，请上传图片后点击识别
                  </div>

                  <!-- 错误 -->
                  <div v-if="errorMsg" class="test-result-error">
                    {{ errorMsg }}
                  </div>
                </div>
                <div v-if="resultText" class="test-result-footer">
                  <van-button type="primary" size="small" @click="copyResult">复制结果</van-button>
                </div>
              </div>
            </div>

            <!-- 接口信息 -->
            <div class="api-info-section">
              <h3 class="section-title">接口说明</h3>
              <div class="info-table">
                <div class="info-row">
                  <span class="info-label">请求地址</span>
                  <span class="info-value api-url">{{ API_BASE }}/recognize_test.php</span>
                </div>
                <div class="info-row">
                  <span class="info-label">请求方式</span>
                  <span class="info-value">POST</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Content-Type</span>
                  <span class="info-value">application/x-www-form-urlencoded</span>
                </div>
              </div>
            </div>

            <!-- 参数说明 -->
            <div class="api-info-section">
              <h3 class="section-title">请求参数</h3>
              <table class="param-table">
                <thead>
                  <tr><th>参数名</th><th>类型</th><th>说明</th><th>必填</th></tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="param-name">image_base64</td>
                    <td>string</td>
                    <td>待识别图片的 Base64 字符串</td>
                    <td><span class="required-yes">是</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">type_code</td>
                    <td>int</td>
                    <td>模型编号：<strong>{{ selectedModel.id }}</strong></td>
                    <td><span class="required-yes">是</span></td>
                  </tr>
                  <tr>
                    <td class="param-name">token</td>
                    <td>string</td>
                    <td>用户 Token（登录后获取）</td>
                    <td><span class="required-yes">是</span></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- 返回参数 -->
            <div class="api-info-section">
              <h3 class="section-title">返回参数</h3>
              <table class="param-table">
                <thead>
                  <tr><th>参数名</th><th>类型</th><th>说明</th></tr>
                </thead>
                <tbody>
                  <tr><td class="param-name">code</td><td>int</td><td>200=成功，其他=失败</td></tr>
                  <tr><td class="param-name">msg</td><td>string</td><td>状态描述</td></tr>
                  <tr><td class="param-name">results</td><td>array</td><td>识别结果数组</td></tr>
                  <tr><td class="param-name">results[].char</td><td>string</td><td>识别出的字符</td></tr>
                  <tr><td class="param-name">results[].confidence</td><td>float</td><td>置信度 (0~1)</td></tr>
                  <tr><td class="param-name">results[].center_x</td><td>int</td><td>X 坐标（坐标类返回）</td></tr>
                  <tr><td class="param-name">results[].center_y</td><td>int</td><td>Y 坐标（坐标类返回）</td></tr>
                  <tr><td class="param-name">results[].bbox</td><td>array</td><td>边界框 [x1,y1,x2,y2]</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <div class="footer">
      <div class="container">
       <p>鱿鱼云码 - 智能验证码识别平台 © 2026 版权所有</p>
      </div>
    </div>
  </div>
</template>

<script>
import Navbar from '@/components/Navbar.vue'
import { Toast } from 'vant'

const API_BASE = 'https://test2.taila.club/api'

export default {
  name: 'Recognition',
  components: { Navbar },
  data() {
    return {
      API_BASE,
      // 列表视图
      categories: [],
      allModels: [],
      filteredModels: [],
      activeCategory: 0,
      keyword: '',
      searchTimer: null,

      // 测试视图
      selectedModel: null,
      selectedFile: null,
      previewUrl: '',
      resultText: '',
      resultImageUrl: '',
      apiResults: [],
      errorMsg: '',
      loading: false
    }
  },
  mounted() {
    this.loadModels()
  },
  methods: {
    // ========== 模型列表 ==========
    async loadModels() {
      try {
        const res = await this.$axios.post(`${API_BASE}/get_models.php`, {}, {
          timeout: 10000,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
        })
        if (res.data.code === 200) {
          this.categories = res.data.categories || []
          this.allModels = res.data.models || []
          this.applyFilter()
        } else {
          Toast(res.data.msg || '加载模型列表失败')
        }
      } catch (e) {
        console.error('[模型列表] 加载失败', e)
        Toast('加载模型列表失败')
      }
    },

    applyFilter() {
      let list = this.allModels
      // 分类筛选
      if (this.activeCategory > 0) {
        list = list.filter(m => m.category_id === this.activeCategory)
      }
      // 关键词搜索
      if (this.keyword) {
        const kw = this.keyword.toLowerCase()
        list = list.filter(m =>
          m.name.toLowerCase().includes(kw) ||
          m.platforms.some(p => p.toLowerCase().includes(kw))
        )
      }
      this.filteredModels = list
    },

    switchCategory(id) {
      this.activeCategory = Number(id)
      this.applyFilter()
    },

    onSearch() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.applyFilter()
      }, 300)
    },

    getCategoryName(categoryId) {
      if (categoryId == 1) return '全部类型'
      const cat = this.categories.find(c => c.id == categoryId)
      return cat ? cat.name : ''
    },

    onImageError(e) {
      e.target.src = 'data:image/svg+xml,' + encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="160" height="100">' +
        '<rect fill="#f5f5f5" width="160" height="100"/>' +
        '<text x="80" y="55" text-anchor="middle" fill="#ccc" font-size="13">加载失败</text></svg>'
      )
    },

    openDocs(model) {
      Toast('开发文档：' + model.name)
      // 后续跳转到文档页面
      // this.$router.push('/docs/' + model.id)
    },

    selectModel(model) {
      this.selectedModel = model
      this.clearImage()
    },

    backToList() {
      this.selectedModel = null
      this.clearImage()
    },

    // ========== 图片上传 ==========
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    handleFileChange(e) {
      const file = e.target.files[0]
      if (file) this.processFile(file)
    },
    handleDrop(e) {
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.processFile(file)
      } else {
        Toast('请上传图片文件')
      }
    },
    processFile(file) {
      if (file.size > 5 * 1024 * 1024) {
        Toast('图片大小不能超过 5MB')
        return
      }
      this.selectedFile = file
      this.resultText = ''
      this.resultImageUrl = ''
      this.apiResults = []
      this.errorMsg = ''
      const reader = new FileReader()
      reader.onload = (e) => { this.previewUrl = e.target.result }
      reader.readAsDataURL(file)
    },
    clearImage() {
      this.selectedFile = null
      this.previewUrl = ''
      this.resultText = ''
      this.resultImageUrl = ''
      this.apiResults = []
      this.errorMsg = ''
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    },

    // ========== 识别请求 ==========
    async doRecognize() {
      if (!this.selectedFile) { Toast('请先上传图片'); return }
      if (!this.selectedModel) return

      this.loading = true
      this.resultText = ''
      this.resultImageUrl = ''
      this.apiResults = []
      this.errorMsg = ''

      try {
        const base64Full = await this.fileToBase64(this.selectedFile)
        const base64Data = base64Full.includes(',') ? base64Full.split(',')[1] : base64Full

        const params = new URLSearchParams()
        params.append('image_base64', base64Data)
        params.append('type_code', this.selectedModel.id)
        params.append('token', localStorage.getItem('token') || '')

        const res = await this.$axios.post(`${API_BASE}/recognize_test.php`, params, {
          timeout: 45000,
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          transformResponse: [data => data]
        })

        let apiData
        if (typeof res.data === 'string') {
          try { apiData = JSON.parse(res.data) } catch (e) {
            throw new Error('服务端返回格式异常')
          }
        } else {
          apiData = res.data
        }

        // console.log('[识别结果]', JSON.stringify(apiData))

        if (apiData.code !== 200) {
          throw new Error(apiData.msg || '识别失败')
        }

        // 兼容多种返回结构：results / data / result
        const results = apiData.results || apiData.data || apiData.result || []
        if (results && results.length > 0) {
          this.apiResults = results
          this.resultText = this.formatResults(results)
          // 用完整 data URL 画标注图
          try {
            await this.generateResultImage(base64Full, results)
          } catch (imgErr) {
            console.warn('[标注图生成失败]', imgErr)
            // 标注失败不影响识别结果展示
          }
          Toast.success('识别成功')
        } else {
          this.resultText = apiData.msg || '未识别到任何目标'
          Toast.success('识别完成')
        }
      } catch (err) {
        console.error('[识别异常]', err)
        const msg = err.response ? `HTTP ${err.response.status}` : (err.message || '识别失败，请重试')
        this.errorMsg = msg
        Toast.fail(msg)
      } finally {
        this.loading = false
      }
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result)
        reader.onerror = reject
        reader.readAsDataURL(file)
      })
    },

    formatResults(results) {
      return results.map((item, index) => {
        const char = item.char || item.text || '-'
        const conf = ((item.confidence || 0) * 100).toFixed(0)
        let line = `目标${index + 1}：${char}  置信度：${conf}%`
        if (item.center_x !== undefined) line += `  X：${item.center_x}`
        if (item.center_y !== undefined) line += `  Y：${item.center_y}`
        return line
      }).join('\n')
    },

    async generateResultImage(base64DataUrl, results) {
      return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = img.width
          canvas.height = img.height
          ctx.drawImage(img, 0, 0)

          // 根据图片尺寸动态计算标注参数
          const lineWidth = Math.max(2, img.width / 300)
          const fontSize = Math.max(12, img.width / 50)
          const dotRadius = Math.max(3, img.width / 250)
          // 如果没有任何 bbox 和坐标，直接返回原图
          const hasAnnot = results.some(r => (r.bbox && r.bbox.length === 4) || (r.center_x !== undefined && r.center_y !== undefined))
          if (!hasAnnot) { resolve(); return }

          // 用不同颜色区分不同目标
          const colors = ['#FF0000', '#0066FF', '#FF6600', '#00AA44', '#AA00FF', '#FF00AA', '#00AAAA', '#AAAA00']
          let colorIdx = 0

          results.forEach((item) => {
            const color = colors[colorIdx % colors.length]
            colorIdx++

            const char = item.char || item.text || ''
            const conf = ((item.confidence || 0) * 100).toFixed(0)
            const label = char ? `${char} (${conf}%)` : `${conf}%`

            // 画 bbox 边界框
            if (item.bbox && item.bbox.length === 4) {
              const [x1, y1, x2, y2] = item.bbox
              ctx.strokeStyle = color
              ctx.lineWidth = lineWidth
              ctx.strokeRect(x1, y1, x2 - x1, y2 - y1)

              // 标签背景 + 文字（在框上方）
              ctx.font = `bold ${fontSize}px Arial`
              const tm = ctx.measureText(label)
              const textH = fontSize * 1.2
              const labelY = y1 - textH - 2 > 0 ? y1 - 2 : y2 + textH + 4
              ctx.fillStyle = color
              ctx.fillRect(x1, labelY - textH - 2, tm.width + 8, textH + 4)
              ctx.fillStyle = '#FFFFFF'
              ctx.fillText(label, x1 + 4, labelY - 4)
            }

            // 画中心点 + 标签（没有 bbox 时也画）
            if (item.center_x !== undefined && item.center_y !== undefined) {
              const cx = item.center_x
              const cy = item.center_y

              // 十字线
              ctx.strokeStyle = color
              ctx.lineWidth = Math.max(1, lineWidth * 0.6)
              ctx.beginPath()
              ctx.moveTo(cx - dotRadius * 3, cy)
              ctx.lineTo(cx + dotRadius * 3, cy)
              ctx.moveTo(cx, cy - dotRadius * 3)
              ctx.lineTo(cx, cy + dotRadius * 3)
              ctx.stroke()

              // 中心圆点
              ctx.beginPath()
              ctx.arc(cx, cy, dotRadius, 0, Math.PI * 2)
              ctx.fillStyle = color
              ctx.fill()
              ctx.strokeStyle = '#FFFFFF'
              ctx.lineWidth = 1
              ctx.stroke()

              // 标签（如果没 bbox 才单独画标签）
              if (!item.bbox || item.bbox.length !== 4) {
                ctx.font = `bold ${fontSize}px Arial`
                const tm = ctx.measureText(label)
                const textH = fontSize * 1.2
                ctx.fillStyle = color
                ctx.fillRect(cx - dotRadius, cy - dotRadius - textH - 8, tm.width + 8, textH + 4)
                ctx.fillStyle = '#FFFFFF'
                ctx.fillText(label, cx - dotRadius + 4, cy - dotRadius - 6)
              }
            }
          })

          this.resultImageUrl = canvas.toDataURL('image/png')
          resolve()
        }
        img.onerror = reject
        img.src = base64DataUrl
      })
    },

    copyResult() {
      if (!this.resultText) return
      navigator.clipboard.writeText(this.resultText).then(() => {
        Toast('已复制到剪贴板')
      }).catch(() => {
        Toast('复制失败')
      })
    }
  }
}
</script>

<style scoped>
.page-wrap { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; }
.recognition-wrapper {
  max-width: 1200px;
  margin: 40px auto;
  padding: 40px 30px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 20px rgba(0,0,0,0.08);
}
.page-title {
  text-align: center;
  font-size: 26px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
}

/* ========== 搜索框 ========== */
.search-box { margin-bottom: 20px; }
.search-input {
  width: 100%;
  padding: 12px 18px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
}
.search-input:focus { border-color: #0066ff; }

/* ========== 分类 Tab ========== */
.category-tabs {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 28px;
}
.tab-item {
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 14px;
  color: #666;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}
.tab-item:hover { color: #0066ff; border-color: #0066ff; }
.tab-item.active { color: #fff; background: #0066ff; border-color: #0066ff; }

/* ========== 模型卡片列表 ========== */
.model-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.model-card {
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.2s;
  background: #fff;
}
.model-card:hover {
  border-color: #0066ff;
  box-shadow: 0 4px 16px rgba(0,102,255,0.1);
}

/* 示例图区域 */
.model-images {
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  background: #fafbfc;
  cursor: pointer;
  flex-wrap: nowrap;
  overflow-x: auto;
}
.sample-img {
  width: 240px;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #eee;
  background: #f5f5f5;
  flex-shrink: 0;
}
.no-images {
  width: 100%;
  padding: 30px;
  text-align: center;
  font-size: 14px;
  color: #bbb;
}

/* 模型名称区 */
.model-header {
  padding: 14px 20px 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.model-name {
  font-size: 18px;
  font-weight: bold;
  color: #222;
}
.model-desc {
  font-size: 14px;
  color: #888;
  margin-left: 8px;
}
.header-platforms {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.header-no-platform {
  font-size: 13px;
  color: #bbb;
}
.desc-text {
  font-size: 14px;
  color: #666;
}

/* 底部信息栏 */
.model-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px 18px;
  border-top: 1px solid #f0f0f0;
  flex-wrap: wrap;
  gap: 12px;
}
.model-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
}
.meta-label {
  color: #999;
  flex-shrink: 0;
}
.meta-value {
  color: #333;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}
.meta-value.price {
  color: #ff6600;
  font-weight: bold;
}
.meta-divider {
  width: 1px;
  height: 16px;
  background: #e0e0e0;
}

/* 平台标签 */
.platform-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  font-size: 12px;
  background: #e8f0fe;
  color: #1a73e8;
}
.platform-chip:nth-child(2) {
  background: #e6f4ea;
  color: #137333;
}
.platform-chip:nth-child(3) {
  background: #fef7e0;
  color: #b06000;
}

/* 操作按钮 */
.model-actions {
  display: flex;
  gap: 8px;
}

/* ========== 空状态 ========== */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: #999;
}
.empty-icon { font-size: 48px; margin-bottom: 16px; }
.empty-text { font-size: 14px; }

/* ========== 测试视图 ========== */
.test-view { padding: 0; }

/* 面包屑 */
.breadcrumb {
  font-size: 13px;
  color: #999;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.breadcrumb a { color: #0066ff; cursor: pointer; text-decoration: none; }
.breadcrumb a:hover { text-decoration: underline; }
.bc-sep { color: #ccc; }
.bc-current { color: #666; }

/* 模型信息卡（左右布局） */
.test-model-card {
  display: flex;
  gap: 30px;
  padding: 24px;
  background: #fafbfc;
  border: 1px solid #e8e8e8;
  border-radius: 10px;
  margin-bottom: 24px;
}
.test-card-left { flex: 1; min-width: 0; }
.test-card-right { flex-shrink: 0; }
.test-model-name { font-size: 22px; font-weight: bold; color: #222; margin: 0 0 12px; }
.test-model-tags { display: flex; gap: 10px; margin-bottom: 12px; flex-wrap: wrap; }
.test-tag {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
}
.type-tag { background: #e8f0fe; color: #1a73e8; }
.price-tag { background: #fff7e0; color: #b06000; font-weight: bold; }
.test-model-desc { font-size: 14px; color: #666; line-height: 1.6; margin-bottom: 12px; }
.test-platforms { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.tp-label { font-size: 13px; color: #999; }
.tp-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  background: #e6f4ea;
  color: #137333;
}

/* 示例图网格（右侧） */
.test-sample-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.test-sample-img {
  width: 140px;
  height: 90px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #eee;
  background: #f5f5f5;
}
.test-no-sample { font-size: 13px; color: #bbb; padding: 20px; text-align: center; }

/* 接口信息区 */
.api-info-section { margin-bottom: 24px; }
.section-title {
  font-size: 17px;
  font-weight: bold;
  color: #333;
  margin: 0 0 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #0066ff;
  display: inline-block;
}

/* 信息表格（请求地址等） */
.info-table { border: 1px solid #e8e8e8; border-radius: 8px; overflow: hidden; }
.info-row {
  display: flex;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 14px;
}
.info-row:last-child { border-bottom: none; }
.info-label {
  width: 120px;
  flex-shrink: 0;
  color: #999;
  font-weight: 500;
}
.info-value { color: #333; word-break: break-all; }
.api-url { color: #0066ff; font-family: 'Courier New', monospace; font-size: 13px; }

/* 参数表格 */
.param-table {
  width: 100%;
  border-collapse: collapse;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  font-size: 14px;
}
.param-table th {
  background: #f5f7fa;
  color: #666;
  font-weight: 500;
  padding: 10px 14px;
  text-align: left;
  border-bottom: 1px solid #e8e8e8;
}
.param-table td {
  padding: 10px 14px;
  border-bottom: 1px solid #f0f0f0;
  color: #333;
}
.param-table tr:last-child td { border-bottom: none; }
.param-name {
  font-family: 'Courier New', monospace;
  color: #0066ff;
  font-weight: 500;
}
.required-yes { color: #e74c3c; font-weight: bold; }

/* 在线测试区域 */
.test-section { background: #f9fafb; padding: 24px; border-radius: 10px; border: 1px solid #e8e8e8; }
.test-section .section-title { margin-top: 0; }

/* 上传区域 */
.test-upload-area {
  width: 100%;
  min-height: 200px;
  border: 2px dashed #d0d5dd;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background: #fff;
  margin-bottom: 16px;
  overflow: hidden;
}
.test-upload-area:hover { border-color: #0066ff; background: #f0f5ff; }
.test-upload-placeholder { text-align: center; }
.upload-svg { width: 48px; height: 48px; margin-bottom: 12px; }
.upload-main-text { font-size: 15px; color: #333; margin: 0 0 6px; }
.upload-sub-text { font-size: 12px; color: #999; margin: 0; }
.test-preview-image { max-width: 100%; max-height: 320px; object-fit: contain; display: block; }

/* 识别按钮 */
.test-action-row { display: flex; gap: 12px; margin-bottom: 20px; }
.test-btn { min-width: 140px; }

/* 识别结果 */
.test-result-area {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.test-result-header {
  background: #f5f7fa;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  border-bottom: 1px solid #e0e0e0;
}
.test-result-body { padding: 16px; }

/* 结果图片 */
.test-result-image-wrap { margin-bottom: 16px; }
.test-result-image { max-width: 100%; max-height: 360px; object-fit: contain; display: block; margin: 0 auto; border-radius: 6px; border: 1px solid #eee; }
.test-result-count { text-align: center; font-size: 13px; color: #666; margin-top: 10px; padding-top: 10px; border-top: 1px solid #f0f0f0; }
.test-result-count strong { color: #0066ff; }

/* 结果列表 */
.test-result-list { display: flex; flex-direction: column; gap: 8px; }
.test-result-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f0f9ff;
  border: 1px solid #b3d8fd;
  border-radius: 6px;
  font-size: 14px;
}
.tri-index { color: #999; font-size: 12px; font-weight: bold; min-width: 30px; }
.tri-char { color: #222; font-weight: bold; font-size: 16px; min-width: 60px; }
.tri-conf { color: #0066ff; font-weight: bold; }
.tri-coord { color: #666; font-size: 12px; margin-left: auto; font-family: 'Courier New', monospace; }

/* 纯文本结果 */
.test-result-text {
  font-size: 18px;
  font-weight: bold;
  color: #222;
  padding: 10px 0;
}

/* 空状态 */
.test-result-empty { color: #bbb; font-size: 14px; text-align: center; padding: 20px 0; }

/* 错误 */
.test-result-error {
  color: #e74c3c;
  font-size: 14px;
  padding: 10px 14px;
  background: #fff5f5;
  border-radius: 6px;
}

/* 结果底部 */
.test-result-footer { padding: 10px 16px; border-top: 1px solid #f0f0f0; text-align: right; }
</style>
